import { Icon } from '@iconify/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { updateTitle } from 'store/app';
import { assetReviews } from 'store/asset';
import { format } from 'date-fns';

const AssetComments = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();

  const { id } = useParams();

  const { reviews } = useSelector(state => state.asset);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd-MM-yyyy');
    } catch (error) {
      return dateString; // fallback to original string if formatting fails
    }
  };

  const fetchDetails = () => {
    dispatch(assetReviews(id))
      .then((res) => {
        if (res.payload.Result) {
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchDetails();
    dispatch(updateTitle(t('review.label', { count: 2 })));
  }, []);

  if (loading || !reviews) {
    return (
      <div className="px-8 flex flex-col gap-8 h-full overflow-y-scroll pb-6 animate-pulse">
        <div className="h-44 bg-gray-300 rounded" />
        <div className="h-44 bg-gray-300 rounded" />
        <div className="h-44 bg-gray-300 rounded" />
      </div>
    );
  }

  return (
    <div className="px-8">
      <div className="flex flex-col gap-5">
        {reviews.map((item, index) =>
          <div key={index} className="rounded-lg bg-[#F4F5F7] py-3 px-4 flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div>
                <div className="h-10 w-10 rounded-full bg-dark flex justify-center items-end">
                  <Icon icon="lucide:user" width="30" className="text-white" />
                </div>
              </div>
              <div className="flex flex-col gap-1 w-full">
                <div className="flex justify-between">
                  <span className="font-semibold">
                    {item?.user?.name}
                  </span>
                  <span className="font-medium text-xs">
                    {formatDate(item?.createdAt)}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    <span className="font-medium text-xs">
                      {t('review.customer_rating')}:
                    </span>
                    <StarRating rating={item?.rating} />
                  </div>
                </div>
              </div>
            </div>
            {item.comment &&
              <div>
                <span className="line-clamp-3">
                  {item.comment}
                </span>
              </div>
            }
          </div>
        )}
      </div>
    </div>
  )
}

export default AssetComments;

const StarRating = ({ rating }) => {
  // Ensure rating is a number and clamp it between 0 and 5
  const numericRating = parseFloat(rating) || 0;
  const clampedRating = Math.max(0, Math.min(5, numericRating));

  // Create an array of 5 stars
  const stars = Array(5).fill(0).map((_, index) => {
    if (index + 1 <= Math.floor(clampedRating)) {
      // Full star
      return <Icon key={index} icon="ic:round-star" className="text-yellow-500" />;
    } else if (index + 0.5 <= clampedRating) {
      // Half star - using a filled star for simplicity
      // You can replace with a half star icon if available
      return <Icon key={index} icon="ic:round-star-half" className="text-yellow-500" />;
    } else {
      // Empty star
      return <Icon key={index} icon="ic:round-star" className="text-gray-300" />;
    }
  });

  return (
    <div className="flex items-center gap-1">
      <div className="flex">{stars}</div>
      <span className="text-xs ml-1">{numericRating.toFixed(1)}</span>
    </div>
  );
};